import React, { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import ProductGrid from "@/components/ProductGrid";
import { fetchProducts } from "@/lib/api";
import useMeta from "@/hooks/useMeta";

const CourseraFeatured = () => (
  <section
    data-testid="coursera-featured"
    className="mb-16 bg-white border border-[#E8DCDD] rounded-md overflow-hidden grid grid-cols-1 lg:grid-cols-12"
  >
    <div className="lg:col-span-5 relative min-h-[280px] lg:min-h-full bg-[#1a1a2e]">
      <img
        src="/api/static/assets/42ddbc58_d8phemsh_image.png"
        alt="Dr. Andrew Campbell speaking on stage in front of an audience"
        className="absolute inset-0 w-full h-full object-cover opacity-90"
        data-testid="coursera-featured-image"
      />
      <div className="absolute top-5 left-5 bg-white/95 text-[#8C4A57] text-[10px] tracking-[0.28em] uppercase font-semibold px-3 py-1.5 rounded-sm">
        Featured · On Coursera
      </div>
    </div>
    <div className="lg:col-span-7 p-8 sm:p-10 flex flex-col justify-center">
      <h2
        data-testid="coursera-featured-title"
        className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2628] mb-4 leading-tight"
      >
        Advanced Leadership in the 21st Century
      </h2>
      <p className="text-sm sm:text-base text-[#6B5E62] leading-relaxed mb-5">
        This course offers an in-depth discussion of the following areas:
      </p>
      <ul className="space-y-2 mb-6">
        {[
          "Understanding the essential leadership responsibilities in an uncertain, complex environment.",
          "Building productive teams in a remote environment.",
          "Addressing strategies for resistance to change.",
        ].map((li, i) => (
          <li key={i} className="flex gap-3 items-start text-[#2D2628] text-sm sm:text-base">
            <span className="text-[#8C4A57] mt-1">•</span>
            <span>{li}</span>
          </li>
        ))}
      </ul>
      <p className="text-sm sm:text-base text-[#6B5E62] leading-relaxed mb-8">
        This course offers seasoned and up-and-coming leaders a clear, concise overview of the
        challenges of organizational leadership and practical strategies for improving as leaders.
        The lessons are structured around essential leadership responsibilities, including defining
        leadership, understanding what it is not, and constructing a leadership framework using the
        elements of the 4D Leadership Development Framework.
      </p>
      <div>
        <a
          href="https://www.coursera.org/specializations/advanced-leadership-skills"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="coursera-featured-cta"
          className="btn-primary inline-flex"
        >
          Enroll on Coursera <ExternalLink className="w-4 h-4 ml-2" />
        </a>
      </div>
    </div>
  </section>
);

const Catalog = ({ type, title, subtitle, before = null }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts({ type }).then((p) => { setProducts(p); setLoading(false); });
  }, [type]);

  return (
    <div data-testid={`catalog-${type}-page`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="mb-12 max-w-2xl">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-3">
          {type === "ebook" ? "Digital Ebooks" : "Video Courses"}
        </div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold text-[#2D2628] mb-4">{title}</h1>
        <p className="text-base text-[#6B5E62] leading-relaxed">{subtitle}</p>
      </div>
      {before}
      {loading ? (
        <div className="text-center text-[#6B5E62] py-20" data-testid="catalog-loading">Loading catalog…</div>
      ) : (
        <ProductGrid products={products} />
      )}
    </div>
  );
};

export const Ebooks = () => {
  useMeta({
    title: "Leadership Ebooks & Workbooks — Global Leader Solutions",
    description: "Downloadable PDF ebooks and workbooks on self-leadership, emotional intelligence, conflict management, and executive character development.",
    canonical: "https://globalleadersolutions.com/ebooks",
  });
  return (
  <Catalog
    type="ebook"
    title="Ebooks & Workbook Libraries"
    subtitle="Practical, downloadable PDFs on leadership, emotional intelligence, self leadership, and conflict management. Every ebook designed to be used, not just read."
  />
);
};

export const Courses = () => {
  useMeta({
    title: "Video Courses & Certificate Programs — Global Leader Solutions",
    description: "Multi-module video courses with companion workbooks, quizzes, and signed certificates. Lifetime access, self-paced, character-based leadership curriculum.",
    canonical: "https://globalleadersolutions.com/courses",
  });
  return (
  <Catalog
    type="course"
    title="Video Courses & Certificate Programs"
    subtitle="Structured, multi-module video courses with companion workbooks and completion certificates. Learn at your own pace with lifetime access."
    before={<CourseraFeatured />}
  />
);
};
